import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { tripService } from '../../services/tripService';
import { tripPDFService } from '../../services/tripPDFService';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PDFPreview from './components/PDFPreview';
import DownloadOptions from './components/DownloadOptions';
import SharingOptions from './components/SharingOptions';
import DownloadHistory from './components/DownloadHistory';
import ActionButtons from './components/ActionButtons';

const PDFGenerationDownload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading } = useAuth();
  
  // State management
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStage, setGenerationStage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [downloadHistory, setDownloadHistory] = useState([]);
  const [tripData, setTripData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoadingTrip, setIsLoadingTrip] = useState(true);

  // Get trip ID from navigation state
  const tripId = location?.state?.tripId;

  const handleLogout = () => {
    // Logout handler - implement based on your auth context
    navigate('/user-login');
  };

  useEffect(() => {
    // Authentication check
    if (!loading && !isAuthenticated) {
      navigate('/user-login', { replace: true });
      return;
    }

    // Load trip data
    const loadTripData = async () => {
      if (!tripId || !user) {
        setError('No trip selected for PDF generation');
        setIsLoadingTrip(false);
        return;
      }

      try {
        setIsLoadingTrip(true);
        const trip = await tripService?.getTripById(tripId);
        
        if (!trip) {
          setError('Trip not found');
          return;
        }

        setTripData(trip);
        setError(null);
      } catch (err) {
        console.error('Error loading trip:', err);
        setError('Failed to load trip data');
      } finally {
        setIsLoadingTrip(false);
      }
    };

    if (user && tripId) {
      loadTripData();
    }
  }, [tripId, user, isAuthenticated, loading, navigate]);

  // PDF generation with enhanced progress tracking
  const handlePDFGeneration = async (format) => {
    if (!tripData) {
      setError('No trip data available for PDF generation');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setError(null);

    try {
      // Stage 1: Preparing data
      setGenerationStage('Preparing trip data...');
      setGenerationProgress(20);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Stage 2: Generating PDF
      setGenerationStage('Generating PDF document...');
      setGenerationProgress(50);

      const pdfBlob = format === 'summary' 
        ? await tripPDFService?.generateSummaryPDF(tripData)
        : await tripPDFService?.generatePDF(tripData);

      // Stage 3: Finalizing
      setGenerationStage('Finalizing download...');
      setGenerationProgress(80);
      await new Promise(resolve => setTimeout(resolve, 300));

      // Complete
      setGenerationProgress(100);
      setGenerationStage('Complete!');

      // Trigger download
      const filename = `${tripData?.title || 'Park_Trip'}_${format === 'detailed' ? 'Detailed' : 'Summary'}.pdf`;
      const url = URL?.createObjectURL(pdfBlob);
      const link = document?.createElement('a');
      link.href = url;
      link.download = filename;
      document?.body?.appendChild(link);
      link?.click();
      document?.body?.removeChild(link);
      URL?.revokeObjectURL(url);

      // Add to download history
      const newDownload = {
        id: Date?.now(),
        filename,
        format: format === 'detailed' ? 'Detailed Itinerary' : 'Quick Summary',
        size: format === 'detailed' ? '4.2 MB' : '1.8 MB',
        downloadDate: new Date(),
        status: 'completed'
      };
      
      setDownloadHistory(prev => [newDownload, ...prev]);

      // Reset after success
      setTimeout(() => {
        setIsGenerating(false);
        setGenerationProgress(0);
        setGenerationStage('');
      }, 1000);

    } catch (err) {
      console.error('PDF generation error:', err);
      setError('Failed to generate PDF. Please try again.');
      setIsGenerating(false);
      setGenerationProgress(0);
      setGenerationStage('');
    }
  };

  const handleDownload = (format) => {
    handlePDFGeneration(format);
  };

  const handleEmailSend = async (email, format) => {
    // Simulate email sending
    await new Promise(resolve => setTimeout(resolve, 2000));
    console.log(`Email sent to ${email} with ${format} format`);
    return true;
  };

  const handleShare = (platform) => {
    console.log(`Shared on ${platform}`);
  };

  const handleSaveTrip = async () => {
    setIsSaving(true);
    
    // Trip is already saved, navigate to dashboard
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSaving(false);
    navigate('/user-dashboard');
  };

  const handleRedownload = (downloadId) => {
    const download = downloadHistory?.find(d => d?.id === downloadId);
    if (download) {
      // Re-trigger the same format download
      const format = download?.format?.includes('Detailed') ? 'detailed' : 'summary';
      handleDownload(format);
    }
  };

  // Loading state
  if (loading || isLoadingTrip) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          isAuthenticated={isAuthenticated} 
          user={user} 
          onLogout={handleLogout} 
        />
        <main className="pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="font-body text-muted-foreground">Loading trip data...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error && !tripData) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          isAuthenticated={isAuthenticated} 
          user={user} 
          onLogout={handleLogout} 
        />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
              <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-2">
                Unable to Load Trip
              </h2>
              <p className="font-body text-muted-foreground mb-6">{error}</p>
              <Button onClick={() => navigate('/user-dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        isAuthenticated={isAuthenticated} 
        user={user} 
        onLogout={handleLogout} 
      />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-primary rounded-organic flex items-center justify-center">
                  <Icon name="FileDown" size={24} color="white" />
                </div>
                <h1 className="text-3xl lg:text-4xl font-heading font-heading-bold text-foreground">
                  Download Your Itinerary
                </h1>
              </div>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Your personalized park trip itinerary is ready! Download, share, or save it to your account for future reference.
              </p>
            </div>

            {/* Enhanced Progress Indicator */}
            {isGenerating && (
              <div className="mt-8 max-w-md mx-auto">
                <div className="bg-card border border-border rounded-organic p-6 shadow-organic-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="animate-spin">
                      <Icon name="Loader2" size={24} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-body font-body-medium text-foreground">
                        {generationStage || 'Generating PDF...'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {Math?.round(generationProgress)}% complete
                      </p>
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-primary h-3 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${generationProgress}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && tripData && (
              <div className="mt-6 max-w-md mx-auto">
                <div className="bg-error/10 border border-error/20 rounded-organic p-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={20} className="text-error" />
                    <p className="text-sm text-error">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Navigation */}
            <div className="mb-6">
              <Button
                variant="ghost"
                size="sm"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate('/user-dashboard')}
                className="text-muted-foreground hover:text-foreground">
                Back to Dashboard
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Preview and Options */}
              <div className="lg:col-span-2 space-y-8">
                {/* PDF Preview */}
                <PDFPreview 
                  tripData={tripData}
                  isGenerating={isGenerating}
                />

                {/* Download Options */}
                <DownloadOptions
                  onDownload={handleDownload}
                  onEmailSend={handleEmailSend}
                  isGenerating={isGenerating}
                />

                {/* Sharing Options */}
                <SharingOptions
                  tripUrl={`${window?.location?.origin}/trip-detail-view/${tripData?.id}`}
                  onShare={handleShare}
                />
              </div>

              {/* Right Column - History and Actions */}
              <div className="space-y-8">
                {/* Action Buttons */}
                <ActionButtons
                  onSaveTrip={handleSaveTrip}
                  isAuthenticated={isAuthenticated}
                  isSaving={isSaving}
                />

                {/* Download History */}
                <DownloadHistory
                  downloads={downloadHistory}
                  onRedownload={handleRedownload}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-12 bg-muted/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-heading font-heading-bold text-foreground mb-4">
                Make the Most of Your Trip
              </h2>
              <p className="text-muted-foreground">
                Tips to enhance your park adventure experience
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card border border-border rounded-organic p-6 text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-organic mx-auto mb-4 flex items-center justify-center">
                  <Icon name="Smartphone" size={24} className="text-primary" />
                </div>
                <h3 className="font-heading font-heading-semibold text-foreground mb-2">
                  Download Offline
                </h3>
                <p className="text-sm text-muted-foreground">
                  Save your PDF for offline access when cell service is limited in remote park areas.
                </p>
              </div>

              <div className="bg-card border border-border rounded-organic p-6 text-center">
                <div className="w-12 h-12 bg-accent/10 rounded-organic mx-auto mb-4 flex items-center justify-center">
                  <Icon name="Users" size={24} className="text-accent" />
                </div>
                <h3 className="font-heading font-heading-semibold text-foreground mb-2">
                  Share with Group
                </h3>
                <p className="text-sm text-muted-foreground">
                  Send the itinerary to your travel companions so everyone stays on the same page.
                </p>
              </div>

              <div className="bg-card border border-border rounded-organic p-6 text-center">
                <div className="w-12 h-12 bg-success/10 rounded-organic mx-auto mb-4 flex items-center justify-center">
                  <Icon name="CheckSquare" size={24} className="text-success" />
                </div>
                <h3 className="font-heading font-heading-semibold text-foreground mb-2">
                  Print & Pack
                </h3>
                <p className="text-sm text-muted-foreground">
                  Print a physical copy as backup and use the packing checklist before departure.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PDFGenerationDownload;